import * as React from 'react';

import { queryClient, sessionToken, setUnauthorizedHandler } from '@/shared/api';

import { useCurrentUser } from '../api/auth.queries';
import type { User } from '../types/auth.types';

interface AuthContextValue {
  /** A token is present. Says nothing about whether it is still valid. */
  isAuthenticated: boolean;
  /** Resolved profile; `null` until `/users/me` answers. */
  user: User | null;
  /** True while the initial profile fetch is in flight. */
  isLoading: boolean;
  /** The profile request failed — usually a dead token or an offline device. */
  error: unknown;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

/** Subscribe a component to the token store held in the API layer. */
function useSessionToken(): string | null {
  return React.useSyncExternalStore(sessionToken.subscribe, sessionToken.get, () => null);
}

/**
 * Owns "who is signed in".
 *
 * The token itself lives in `shared/api` (the axios interceptor needs it
 * synchronously); this provider turns it into React state and resolves it to a
 * profile. Routing guards read `isAuthenticated`, screens read `user`.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const token = useSessionToken();
  const isAuthenticated = token !== null;

  const userQuery = useCurrentUser({ enabled: isAuthenticated });

  const logout = React.useCallback(() => {
    sessionToken.clear();
    // Every cached response belongs to the session that fetched it.
    queryClient.clear();
  }, []);

  // A rejected token must end the session wherever it is detected — including
  // inside a background refetch the user never triggered.
  React.useEffect(() => {
    setUnauthorizedHandler(() => queryClient.clear());
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      user: userQuery.data ?? null,
      isLoading: isAuthenticated && userQuery.isPending,
      error: userQuery.error,
      logout,
    }),
    [isAuthenticated, userQuery.data, userQuery.isPending, userQuery.error, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = React.use(AuthContext);
  if (!context) throw new Error('useAuth must be used within an <AuthProvider>');
  return context;
}
